import type { AdminProfile } from '../types/api';

/**
 * Session storage and lifetime.
 *
 * The API issues admin tokens with an 8-hour TTL (`JWT_ADMIN_TTL`). The console
 * keeps the token in localStorage so a reload does not force a re-login, and
 * reads the JWT's own `exp` claim so it can expire the session locally rather
 * than waiting for the first 401 of the next working day.
 *
 * The token is still the only thing the server trusts — everything decoded here
 * is for the UI's benefit. A tampered `exp` buys nothing, because the server
 * verifies the signature on every request.
 */

const TOKEN_KEY = 'bo.token';
const PROFILE_KEY = 'bo.admin';
const REASON_KEY = 'bo.signout-reason';

/**
 * Why a session ended. The API distinguishes an inactivity timeout from a
 * revoked session, and so should the message the operator reads.
 */
export type SignOutReason =
  | 'expired'
  | 'idle'
  | 'rejected'
  /** The account signed in somewhere else — one account holds one session. */
  | 'superseded'
  | 'manual';

export interface JwtClaims {
  sub: string;
  aud: string;
  role?: string;
  exp: number;
  iat: number;
}

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string | null) {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    /* private browsing — the session lasts for this tab only */
  }
}

/** Decodes a JWT payload without verifying it. Returns null if unreadable. */
export function decodeToken(token: string): JwtClaims | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const normalised = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalised.padEnd(normalised.length + ((4 - (normalised.length % 4)) % 4), '=');
    const claims = JSON.parse(atob(padded)) as JwtClaims;
    return typeof claims.exp === 'number' ? claims : null;
  } catch {
    return null;
  }
}

/** Milliseconds until the token expires. Negative when already expired. */
export function millisUntilExpiry(token: string): number | null {
  const claims = decodeToken(token);
  if (!claims) return null;
  return claims.exp * 1000 - Date.now();
}

export function isExpired(token: string): boolean {
  const remaining = millisUntilExpiry(token);
  // A token we cannot read is left to the server to reject — we do not guess.
  return remaining !== null && remaining <= 0;
}

export const getToken = () => safeGet(TOKEN_KEY);

export function getValidToken(): string | null {
  const token = getToken();
  if (!token) return null;
  if (isExpired(token)) {
    clearSession('expired');
    return null;
  }
  return token;
}

export function getProfile(): AdminProfile | null {
  const raw = safeGet(PROFILE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AdminProfile;
  } catch {
    return null;
  }
}

export function saveSession(token: string, profile: AdminProfile) {
  safeSet(TOKEN_KEY, token);
  safeSet(PROFILE_KEY, JSON.stringify(profile));
  safeSet(REASON_KEY, null);
}

export function clearSession(reason: SignOutReason = 'manual') {
  const hadReason = safeGet(REASON_KEY);

  safeSet(TOKEN_KEY, null);
  safeSet(PROFILE_KEY, null);

  if (reason === 'manual') {
    safeSet(REASON_KEY, null);
    return;
  }

  // First cause wins. An expired token makes in-flight requests fail with 401,
  // and that follow-on 'rejected' must not overwrite the true reason — the
  // operator should read "your session timed out", not "no longer valid".
  if (!hadReason) safeSet(REASON_KEY, reason);
}

export function takeSignOutReason(): SignOutReason | null {
  const reason = safeGet(REASON_KEY) as SignOutReason | null;
  if (reason) safeSet(REASON_KEY, null);
  return reason;
}

/**
 * Notifies when the session changes in *another* tab, so signing out once signs
 * out everywhere rather than leaving a second tab believing it is still live.
 */
export function onSessionChangedElsewhere(listener: (hasToken: boolean) => void) {
  const handler = (event: StorageEvent) => {
    if (event.key !== TOKEN_KEY) return;
    listener(Boolean(event.newValue));
  };
  window.addEventListener('storage', handler);
  return () => window.removeEventListener('storage', handler);
}
